/**
 * Preferences Slice - Redux slice for user i18n and unit preferences
 * Manages locale, temperature, area, precipitation, volume, and pressure unit preferences
 */

import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { 
  UserPreferences, 
  UserPreferencesUpdate,
  TemperatureUnit,
  AreaUnit,
  PrecipitationUnit,
  VolumeUnit,
  PressureUnit
} from '../../types/api';
import { logger } from '../../utils/logger';
import type { AppDispatch, RootState } from '../index';

// Preferences state interface
export interface PreferencesState {
  preferences: UserPreferences;
  isLoading: boolean;
  error: string | null;
  updateSuccess: boolean;
}

// Default preferences
const DEFAULT_PREFERENCES: UserPreferences = {
  locale: 'en',
  temperature_unit: 'C',
  area_unit: 'metric',
  precipitation_unit: 'mm',
  volume_unit: 'liters',
  pressure_unit: 'bar',
};

// Initial state
const initialState: PreferencesState = {
  preferences: DEFAULT_PREFERENCES,
  isLoading: false,
  error: null,
  updateSuccess: false,
};

/**
 * Preferences slice definition
 */
const preferencesSlice = createSlice({
  name: 'preferences',
  initialState,
  reducers: {
    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    // Set error message
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Set preferences (from API response)
    setPreferences: (state, action: PayloadAction<UserPreferences>) => {
      state.preferences = action.payload;
      state.isLoading = false;
      state.error = null;
    },

    // Update individual preference fields
    updatePreference: <K extends keyof UserPreferences>(
      state: PreferencesState,
      action: PayloadAction<{ key: K; value: UserPreferences[K] }>
    ) => {
      state.preferences[action.payload.key] = action.payload.value;
    },

    // Set update success flag
    setUpdateSuccess: (state, action: PayloadAction<boolean>) => {
      state.updateSuccess = action.payload;
    },

    // Reset preferences to default
    resetPreferences: (state) => {
      state.preferences = DEFAULT_PREFERENCES;
      state.error = null;
      state.updateSuccess = false;
    },

    // Clear update success state
    clearUpdateSuccess: (state) => {
      state.updateSuccess = false;
    },
  },
});

// Export actions
export const {
  setLoading,
  setError,
  clearError,
  setPreferences,
  updatePreference,
  setUpdateSuccess,
  resetPreferences,
  clearUpdateSuccess,
} = preferencesSlice.actions;

/**
 * Async thunk to fetch user preferences from API
 */
export const fetchPreferencesAsync = () => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoading(true));
      dispatch(clearError());

      logger.debug('Fetching user preferences...');

      // Import apiClient dynamically to avoid circular dependencies
      const { apiClient } = await import('../../services/apiClient');

      try {
        const response = await apiClient.get('/auth/me/preferences');
        logger.debug('Preferences fetched successfully', response);

        dispatch(setPreferences(response));
        
        // Update i18next language if locale changed
        const { default: i18n } = await import('../../i18n');
        if (response.locale && i18n.language !== response.locale) {
          await i18n.changeLanguage(response.locale);
          logger.debug('Language changed to', response.locale);
        }

        return { success: true, preferences: response };
      } catch (apiError) {
        logger.error('API Error fetching preferences', apiError);
        const errorMessage = apiError instanceof Error ? apiError.message :
                           (apiError as { message?: string })?.message || 'Failed to fetch preferences';
        dispatch(setError(errorMessage));
        throw apiError;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch preferences';
      dispatch(setError(errorMessage));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };
};

/**
 * Async thunk to update user preferences
 */
export const updatePreferencesAsync = (updates: UserPreferencesUpdate) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoading(true));
      dispatch(clearError());
      dispatch(setUpdateSuccess(false));

      logger.debug('Updating user preferences', updates);

      // Import apiClient dynamically to avoid circular dependencies
      const { apiClient } = await import('../../services/apiClient');

      try {
        const response = await apiClient.put('/auth/me/preferences', updates);
        logger.debug('Preferences updated successfully', response);

        dispatch(setPreferences(response));
        dispatch(setUpdateSuccess(true));

        // Update i18next language if locale was changed
        if (updates.locale) {
          const { default: i18n } = await import('../../i18n');
          if (i18n.language !== updates.locale) {
            await i18n.changeLanguage(updates.locale);
            logger.debug('Language changed to', updates.locale);
          }
        }

        // Update user in auth state if needed
        const { authService } = await import('../../services/authService');
        const currentUser = authService.getCurrentUser();
        if (currentUser) {
          const updatedUser = { ...currentUser, ...updates };
          authService.updateUser(updatedUser);
        }

        return { success: true, preferences: response };
      } catch (apiError) {
        logger.error('API Error updating preferences', apiError);
        const errorMessage = apiError instanceof Error ? apiError.message :
                           (apiError as { message?: string })?.message || 'Failed to update preferences';
        dispatch(setError(errorMessage));
        throw apiError;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update preferences';
      dispatch(setError(errorMessage));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };
};

/**
 * Thunk to initialize preferences from authenticated user
 */
export const initializePreferencesFromUser = () => {
  return async (dispatch: AppDispatch) => {
    try {
      const { authService } = await import('../../services/authService');
      const { default: i18n } = await import('../../i18n');
      const currentUser = authService.getCurrentUser();

      if (currentUser) {
        // Extract preferences from user object
        const userPreferences: UserPreferences = {
          // No stored choice: keep what the browser detection resolved to
          // rather than forcing English.
          locale: currentUser.locale
            || (i18n.resolvedLanguage || i18n.language || '').split('-')[0]
            || 'es',
          temperature_unit: (currentUser.temperature_unit as TemperatureUnit) || DEFAULT_PREFERENCES.temperature_unit,
          area_unit: (currentUser.area_unit as AreaUnit) || DEFAULT_PREFERENCES.area_unit,
          precipitation_unit: (currentUser.precipitation_unit as PrecipitationUnit) || DEFAULT_PREFERENCES.precipitation_unit,
          volume_unit: (currentUser.volume_unit as VolumeUnit) || DEFAULT_PREFERENCES.volume_unit,
          pressure_unit: (currentUser.pressure_unit as PressureUnit) || DEFAULT_PREFERENCES.pressure_unit,
        };

        logger.debug('Initializing preferences from user', userPreferences);
        dispatch(setPreferences(userPreferences));

        // Update i18next language
        if (userPreferences.locale && i18n.language !== userPreferences.locale) {
          await i18n.changeLanguage(userPreferences.locale);
          logger.debug('Language initialized to', userPreferences.locale);
        }
      }
    } catch (error) {
      logger.error('Error initializing preferences from user', error);
    }
  };
};

// Export reducer
export default preferencesSlice.reducer;

// ============================================================================
// SELECTORS
// ============================================================================

/**
 * Base selectors
 */
export const selectPreferencesState = (state: RootState) => state.preferences;
export const selectPreferences = (state: RootState) => state.preferences.preferences;
export const selectPreferencesLoading = (state: RootState) => state.preferences.isLoading;
export const selectPreferencesError = (state: RootState) => state.preferences.error;
export const selectPreferencesUpdateSuccess = (state: RootState) => state.preferences.updateSuccess;

/**
 * Individual preference selectors
 */
export const selectLocale = (state: RootState) => state.preferences.preferences.locale;
export const selectTemperatureUnit = (state: RootState) => state.preferences.preferences.temperature_unit;
export const selectAreaUnit = (state: RootState) => state.preferences.preferences.area_unit;
export const selectPrecipitationUnit = (state: RootState) => state.preferences.preferences.precipitation_unit;
export const selectVolumeUnit = (state: RootState) => state.preferences.preferences.volume_unit;
export const selectPressureUnit = (state: RootState) => state.preferences.preferences.pressure_unit;

/**
 * Memoized selectors for derived data
 */

// Check if using imperial units (for area)
export const selectIsImperialArea = createSelector(
  [selectAreaUnit],
  (areaUnit) => areaUnit === 'imperial'
);

// Check if using Fahrenheit
export const selectIsFahrenheit = createSelector(
  [selectTemperatureUnit],
  (tempUnit) => tempUnit === 'F'
);

// Check if using inches for precipitation
export const selectIsInches = createSelector(
  [selectPrecipitationUnit],
  (precipUnit) => precipUnit === 'inches'
);

// Check if using US gallons
export const selectIsGallonsUS = createSelector(
  [selectVolumeUnit],
  (volumeUnit) => volumeUnit === 'gallons_us'
);

// Check if using UK gallons
export const selectIsGallonsUK = createSelector(
  [selectVolumeUnit],
  (volumeUnit) => volumeUnit === 'gallons_uk'
);

// Check if using PSI for pressure
export const selectIsPSI = createSelector(
  [selectPressureUnit],
  (pressureUnit) => pressureUnit === 'psi'
);

// Get all unit preferences as a single object
export const selectUnitPreferences = createSelector(
  [selectTemperatureUnit, selectAreaUnit, selectPrecipitationUnit, selectVolumeUnit, selectPressureUnit],
  (temperature, area, precipitation, volume, pressure) => ({
    temperature,
    area,
    precipitation,
    volume,
    pressure,
  })
);

// Check if preferences have been customized (differ from defaults)
export const selectHasCustomPreferences = createSelector(
  [selectPreferences],
  (preferences) => {
    return Object.keys(preferences).some((key) => {
      const prefKey = key as keyof UserPreferences;
      return preferences[prefKey] !== DEFAULT_PREFERENCES[prefKey];
    });
  }
);
